import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const DOMESTIC_ZIP_PATTERN = /^[0-9-]*$/;
const INTERNATIONAL_ZIP_PATTERN = /^[a-zA-Z0-9 -]*$/;

/**
 * Strip characters a zip/postal code input should not accept.
 * Domestic (US) allows digits and dashes; international additionally
 * allows letters and spaces (e.g. Canadian "V6Z 2G7").
 */
export function sanitizeZipInput(
  value: string,
  isInternational: boolean,
): string {
  return isInternational
    ? value.replace(/[^a-zA-Z0-9 -]/g, '')
    : value.replace(/[^0-9-]/g, '');
}

/**
 * Zip/postal code pattern validator that follows the sibling
 * `isInternational` control in the same form group: digits and dashes for
 * domestic addresses, letters/digits/spaces/dashes for international ones.
 */
export function internationalAwareZipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value, let the required validator handle it
    if (!value || typeof value !== 'string') {
      return null;
    }

    const isInternational = !!control.parent?.get('isInternational')?.value;
    const pattern = isInternational
      ? INTERNATIONAL_ZIP_PATTERN
      : DOMESTIC_ZIP_PATTERN;

    return pattern.test(value)
      ? null
      : {
          pattern: { requiredPattern: pattern.toString(), actualValue: value },
        };
  };
}

/**
 * Custom validator for country-specific zip code validation
 * @param countryCode - The country code (codeA3) or country name
 * @param getZipPattern - Function to get the zip pattern for a country
 * @returns ValidatorFn
 */
export function zipCodeValidator(
  countryCode: string | (() => string),
  testZipCode: (zipCode: string, country: string) => boolean,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value, let the required validator handle it
    if (!value) {
      return null;
    }

    // Get the country code (either from string or function)
    const country =
      typeof countryCode === 'function' ? countryCode() : countryCode;

    if (!country) {
      return null;
    }

    const isValid = testZipCode(value, country);

    return isValid ? null : { invalidZipCode: { value, country } };
  };
}

/**
 * Dynamic zip code validator that updates when country changes
 * Use this when the country is part of the same form group
 */
export function dynamicZipCodeValidator(
  testZipCode: (zipCode: string, country: string) => boolean,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value, let the required validator handle it
    if (!value) {
      return null;
    }

    // Get the country from the parent form group
    const parent = control.parent;
    if (!parent) {
      return null;
    }

    const isSameAddressControl = parent.get('isSameAddress');
    if (isSameAddressControl && isSameAddressControl.value === true) {
      return null;
    }

    // International addresses accept the generic postal charset (letters,
    // digits, spaces and dashes) regardless of the selected country. Only
    // domestic addresses keep country-specific format validation.
    if (parent.get('isInternational')?.value) {
      return INTERNATIONAL_ZIP_PATTERN.test(value)
        ? null
        : { invalidZipCode: { value, country: parent.get('country')?.value } };
    }

    const countryControl = parent.get('country');
    const country = countryControl?.value;

    if (!country) {
      // If the country control is explicitly failing a required validator,
      // let that validator surface the error instead of adding a zip error.
      if (
        countryControl &&
        countryControl.errors &&
        countryControl.errors['required']
      ) {
        return null;
      }

      // Otherwise, a non-empty zip code with no country should not silently pass validation.
      return { invalidZipCode: { value, country } };
    }

    const isValid = testZipCode(value, country);

    return isValid ? null : { invalidZipCode: { value, country } };
  };
}
